import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router";
import {
    OnboardingLayout,
    OnboardingDataCard,
    EditInput,
    type OnboardingStep,
    type BadgeStatus,
} from "@vanyshr/ui/components/onboarding";
import { cx } from "@/utils/cx";
import { Mail, Plus } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { triggerBreachesScan } from "@/lib/breaches";
import { allowLocalRouteBypass } from "@/lib/env";
import { patchDevProgress } from "@/lib/dev-user";
import { loadLocalOnboardingSeed } from "@/lib/local-onboarding-seed";

export interface EmailItem {
    id: string;
    email: string;
    status: BadgeStatus;
    isPrimary: boolean;
    isAuthEmail?: boolean; // auth email — cannot be deleted
    isNew?: boolean;
}

export function OnboardingEmails() {
    const [items, setItems] = useState<EmailItem[]>([]);
    const [profileId, setProfileId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [activeId, setActiveId] = useState<string | null>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editValue, setEditValue] = useState("");
    const [editError, setEditError] = useState<string | null>(null);

    const navigate = useNavigate();

    // -----------------------------------------------------------------------
    // Load from DB
    // -----------------------------------------------------------------------
    useEffect(() => {
        async function load() {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                if (allowLocalRouteBypass()) {
                    const seed = loadLocalOnboardingSeed();
                    setItems((seed?.emails ?? []).map((email, i) => ({
                        id: `local-email-${i}`,
                        email,
                        status: "pending" as BadgeStatus,
                        isPrimary: i === 0,
                    })));
                }
                setIsLoading(false);
                return;
            }

            const { data: profile } = await supabase
                .from("user_profiles")
                .select("id, email")
                .eq("auth_user_id", user.id)
                .single();

            if (!profile) { setIsLoading(false); return; }
            setProfileId(profile.id);

            const { data: emails } = await supabase
                .from("user_emails")
                .select("id, email, is_primary, user_confirmed_status, source")
                .eq("user_id", profile.id)
                .eq("is_active", true)
                .order("is_primary", { ascending: false });

            if (emails) {
                setItems(emails.map((e) => ({
                    id:           e.id,
                    email:        e.email,
                    isPrimary:    e.is_primary,
                    isAuthEmail:  e.source === "auth",
                    status:       e.user_confirmed_status === "confirmed" ? "confirmed" : "pending" as BadgeStatus,
                })));
            }
            setIsLoading(false);
        }
        load();
    }, []);

    // -----------------------------------------------------------------------
    // DB helpers
    // -----------------------------------------------------------------------
    const upsertEmail = useCallback(async (item: EmailItem) => {
        if (!profileId) return;
        const now = new Date().toISOString();
        if (item.isNew) {
            const { data } = await supabase
                .from("user_emails")
                .insert({
                    user_id:               profileId,
                    email:                 item.email,
                    is_primary:            item.isPrimary,
                    user_confirmed_status: "confirmed",
                    confirmed_at:          now,
                    source:                "user_input",
                })
                .select("id")
                .single();
            if (data) {
                setItems((prev) =>
                    prev.map((e) => e.id === item.id ? { ...e, id: data.id, isNew: false } : e)
                );
            }
        } else {
            await supabase
                .from("user_emails")
                .update({
                    email:                 item.email,
                    is_primary:            item.isPrimary,
                    user_confirmed_status: "confirmed",
                    confirmed_at:          now,
                })
                .eq("id", item.id);
        }
    }, [profileId]);

    const deleteEmail = useCallback(async (id: string, isNew?: boolean) => {
        if (isNew) return;
        const { error } = await supabase
            .from("user_emails")
            .update({ is_active: false })
            .eq("id", id);
        if (error) console.error("[Onboarding] Email delete failed:", error.message);
    }, []);

    const updateAllPrimary = useCallback(async (primaryId: string) => {
        if (!profileId) return;
        await supabase
            .from("user_emails")
            .update({ is_primary: false })
            .eq("user_id", profileId);
        await supabase
            .from("user_emails")
            .update({ is_primary: true })
            .eq("id", primaryId);
    }, [profileId]);

    // -----------------------------------------------------------------------
    // Interaction handlers
    // -----------------------------------------------------------------------
    const openEdit = (item: EmailItem) => {
        if (item.isAuthEmail) return; // auth email is confirmed — no editing
        setActiveId(item.id);
        setEditingId(item.id);
        setEditValue(item.email);
        setEditError(null);
    };

    const isValidEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

    const handleUpdate = async (id: string) => {
        const trimmed = editValue.trim();
        if (!isValidEmail(trimmed)) {
            setEditError("Please enter a valid email address.");
            return;
        }
        setEditError(null);
        const updated = items.map((e) =>
            e.id === id
                ? { ...e, email: trimmed, status: "confirmed" as BadgeStatus }
                : e,
        );
        setItems(updated);
        setEditingId(null);
        setActiveId(null);

        const item = updated.find((e) => e.id === id);
        if (item) await upsertEmail(item);
    };

    const handleTogglePrimary = async (id: string, isPrimary: boolean) => {
        setItems((prev) => prev.map((e) => ({ ...e, isPrimary: e.id === id ? isPrimary : false })));
        if (isPrimary) await updateAllPrimary(id);
    };

    const handleDelete = async (id: string) => {
        const item = items.find((e) => e.id === id);
        if (item?.isAuthEmail) return; // never delete the auth email
        setItems((prev) => prev.filter((e) => e.id !== id));
        if (activeId === id) setActiveId(null);
        setEditingId(null);
        if (item) await deleteEmail(id, item.isNew);
    };

    const handleAdd = () => {
        const tempId = `new-${Date.now()}`;
        setItems((prev) => [...prev, {
            id:        tempId,
            email:     "",
            isPrimary: false,
            status:    "pending",
            isNew:     true,
        }]);
        setActiveId(tempId);
        setEditingId(tempId);
        setEditValue("");
    };

    const handleConfirmAndContinue = async () => {
        setIsSaving(true);
        if (profileId) {
            await supabase
                .from("user_emails")
                .update({
                    user_confirmed_status: "confirmed",
                    confirmed_at:          new Date().toISOString(),
                })
                .eq("user_id", profileId)
                .eq("is_active", true);

            triggerBreachesScan(profileId);
        }
        patchDevProgress({ step: 2 });
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
            await supabase
                .from("user_profiles")
                .update({ onboarding_step: 2 })
                .eq("auth_user_id", session.user.id);

            // Fire-and-forget HIBP breach scan — runs in background after emails
            // are confirmed. Results appear on the dashboard.
            if (profileId) {
                fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/breaches-scan`, {
                    method:  "POST",
                    headers: {
                        "Content-Type":  "application/json",
                        "Authorization": `Bearer ${session.access_token}`,
                    },
                    body: JSON.stringify({ profile_id: profileId }),
                }).catch(err => console.error("[Onboarding] breach scan trigger failed:", err));
            }
        }
        setIsSaving(false);
        navigate("/onboarding/phone-numbers");
    };

    if (isLoading) {
        return (
            <OnboardingLayout
                currentStep={"emails" satisfies OnboardingStep}
                completedSteps={["basic"]}
                title="Emails"
                subtitle="Click on Field to Edit"
                onDashboardNavigate={() => navigate("/scanning-started")}
                footer={null}
            >
                <div className="flex items-center justify-center py-16">
                    <div className="w-6 h-6 rounded-full border-2 border-[#14ABFE] border-t-transparent animate-spin" />
                </div>
            </OnboardingLayout>
        );
    }

    return (
        <OnboardingLayout
            currentStep={"emails" satisfies OnboardingStep}
            completedSteps={["basic"]}
            title="Emails"
            subtitle="Click on Field to Edit"
            onDashboardNavigate={() => navigate("/scanning-started")}
            footer={
                <div className="flex w-full gap-3">
                    <button
                        type="button"
                        onClick={() => navigate("/onboarding/primary-info")}
                        className={cx(
                            "flex h-[52px] flex-1 items-center justify-center rounded-xl text-sm font-semibold outline-none transition",
                            "border border-[var(--border-subtle)] dark:border-[#1E3A52]",
                            "text-[#0B1B2B] dark:text-white bg-transparent hover:bg-state-hover dark:hover:bg-white/10",
                            "focus-visible:ring-2 focus-visible:ring-[#14ABFE] focus-visible:ring-offset-2 dark:focus-visible:ring-offset-[#0B1B2B]",
                        )}
                    >
                        &lt; BACK
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirmAndContinue}
                        disabled={isSaving}
                        className={cx(
                            "flex h-[52px] flex-1 items-center justify-center rounded-xl text-sm font-semibold text-white outline-none transition",
                            "bg-[#14ABFE] hover:bg-[#0E9AE8]",
                            "focus-visible:ring-2 focus-visible:ring-[#14ABFE] focus-visible:ring-offset-2 dark:focus-visible:ring-offset-[#0B1B2B]",
                            isSaving && "opacity-60 cursor-not-allowed",
                        )}
                    >
                        {isSaving ? "Saving..." : "CONFIRM >"}
                    </button>
                </div>
            }
        >
            {items.length === 0 && (
                <div className={cx(
                    "rounded-xl border p-6 text-center",
                    "bg-[var(--bg-surface)] dark:bg-[#112538]",
                    "border-[var(--border-subtle)] dark:border-[#1E3A52]",
                )}>
                    <p className="text-sm text-[var(--text-secondary)] dark:text-[#94A3B8] mb-4">
                        No additional emails found. Your sign-in email is already tracked. Add any other email addresses linked to your identity.
                    </p>
                </div>
            )}

            <div className="flex flex-col gap-4">
                {items.map((item) => (
                    <OnboardingDataCard
                        key={item.id}
                        value={item.email}
                        status={item.status}
                        isExpanded={activeId === item.id}
                        isEditing={editingId === item.id}
                        editContent={
                            editingId === item.id && !item.isAuthEmail ? (
                                <EditInput
                                    id={`email-${item.id}`}
                                    label="Email address"
                                    value={editValue}
                                    onChange={(v) => { setEditValue(v); setEditError(null); }}
                                    placeholder="you@example.com"
                                    type="email"
                                    icon={Mail}
                                    error={editError ?? undefined}
                                />
                            ) : undefined
                        }
                        onClick={() => activeId === item.id ? setActiveId(null) : openEdit(item)}
                        onConfirmAndContinue={() => handleUpdate(item.id)}
                        toggleLabel="Primary"
                        toggleValue={item.isPrimary}
                        onToggleChange={(v) => handleTogglePrimary(item.id, v)}
                        showDelete={!item.isAuthEmail}
                        onDelete={() => handleDelete(item.id)}
                    />
                ))}
            </div>

            <div className="mt-8 flex justify-center pb-4">
                <button
                    type="button"
                    onClick={handleAdd}
                    className={cx(
                        "flex h-14 w-14 items-center justify-center rounded-full outline-none transition shadow-lg",
                        "bg-[#00D4AA] text-[#0B1B2B] hover:bg-[#00E5B8]",
                        "focus-visible:ring-2 focus-visible:ring-[#00D4AA] focus-visible:ring-offset-2 dark:focus-visible:ring-offset-[#0B1B2B]",
                    )}
                    aria-label="Add Email"
                >
                    <Plus className="h-6 w-6" aria-hidden />
                </button>
            </div>
        </OnboardingLayout>
    );
}
